/* eslint-disable no-unused-vars */
import { useEffect, useRef, useState } from "react";
import LoadingSpinner from "../LoadingSpinner";
import { notifier } from "../../utils/notifier";
import { useTranslation } from "react-i18next";
import { encodeParamsHmacBase64 } from "../../utils/encodeParamsHmacBase64";
import { useSelector } from "react-redux";
import { useUser } from "../../store/slices/userSlice";
import ThreeDSZidpayFrame from "./ThreeDSZidpayFrame";

const PaymentProviders = {
  myfatoorah: 1,
  moyasar: 3
};

function ZidPayPayment ({
  currency,
  amount,
  handlePayment,
  loadingInit,
  isUpdateMethod = false,
  subscriptionId,
  costId
}) {
  const zidPayData = useSelector(state => state.configStore.merchant.is_zidpay_enabled);
  const user = useUser();
  const [sendPaymentLoading, setSendPaymentLoading] = useState(false);
  const { t } = useTranslation(["translation"]);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const isLoadedRef = useRef(false); // Prevents duplicate
  const [ready, setReady] = useState(false);
  const paymentSessionIDRef = useRef(crypto.randomUUID());
  const paymentSessionID = paymentSessionIDRef.current;
  const [redirectUrl, setRedirectUrl] = useState(null);

  function handleRedirect (url) {
    // window.location.href = url;
    console.log("3D Secure redirect to:", url);
  }
  const handleSendPayment = async () => {
    try {
      setSendPaymentLoading(true);
      const returnValue = await window.zidpayPaymentForm.processPayment();
      if (returnValue?.isSuccess === true && returnValue?.data) {
        handlePayment("zidpay", null, null, true, null, paymentSessionID);
        if (returnValue?.paymentProvider === PaymentProviders.myfatoorah) {
          // myfatoorah 3d secure iframe
          setRedirectUrl(returnValue.data);
        } else if (returnValue?.paymentProvider === PaymentProviders.moyasar) {
          // moyasar 3d secure redirect
          window.location.href = returnValue.data;
        } else {
          notifier.error("Unknown payment provider");
        }
      } else {
        notifier.error("Payment failed. Please try again later");
        setSendPaymentLoading(false);
      }
    } catch (error) {
      setSendPaymentLoading(false);
      console.error("Payment failed:", error);
    }
  };
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data) return;

      try {
        const message = JSON.parse(event.data);

        if (message.sender === "MF-3DSecure") {
          const url = message.url;
          // 👉 Do your action here
          // Example: redirect
          if (url) {
            window.location.href = url;
          }

          // OR update React state
          // setRedirectUrl(url);

          // OR close iframe / show loader / fetch status
        }
      } catch (err) {
        // ignore invalid JSON
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (isLoadedRef.current) return; // Guard!
    if (!zidPayData) return;
    if (!user?.zid_external_id) return;
    isLoadedRef.current = true;

    const port = window.location.port ? `:${window.location.port}` : "";
    const currentBaseUrl = window.location.origin || `${window.location.protocol}//${window.location.host}${port?.toString() || ""}`;
    const testBaseUrlForWebhook = "https://d4438f02cc1f.ngrok-free.app";
    const finalBaseUrlForWebhook = currentBaseUrl.includes("localhost") ? testBaseUrlForWebhook : currentBaseUrl;
    const finalWebhookUrl = `${finalBaseUrlForWebhook}/api/zidpay/webhook/?sm-payment-sessionid=${paymentSessionID}`;
    const customerEmail = user?.email || `user-${user?.id}@eshtarek.com`;
    // const customerEmail = "mohammed.majed@zid.sa";
    const createPaymentParam = {
      PaymentMethod: 2,
      CallBackUrl: `${currentBaseUrl}/checkout/?success=true&cost=${costId}&isZidPay=true&paymentSessionID=${paymentSessionID}`,
      ErrorUrl: `${currentBaseUrl}/checkout/?success=false&cost=${costId}&?failed=1&isZidPay=true&paymentSessionID=${paymentSessionID}`,
      WebhookUrl: finalWebhookUrl,
      // CustomerMobileCountryCode: "20",
      // CustomerMobile: "1157366916",
      CustomerEmail: customerEmail,
      // CustomerReference: "39915",
      CustomerReference: user?.zid_external_id,
      CustomerName: "User",
      CustomerMobileCountryCode: user?.phone_country_code,
      CustomerMobile: user?.phone_number_as_national,
      CustomerCountryCode: "SA",
      DisplayCurrencyIso: currency,
      Amount: amount,
      MerchantReferenceId: zidPayData?.gateway_merchant_id,
      CountryCode: "SA",
      Partner: "Eshtarek"
    };
    const config = {
      containerElementId: "zidpay-payment-form-container-block",
      language: "en",
      countryCode: "SA",
      currency,
      zidpayMerchantId: zidPayData?.gateway_merchant_id
    };

    const zidpayErrorCallback = (error) => {
      console.log("zidpayErrorCallback", error);
    };
    const applePayButtonClickCallback = (error) => {
      console.log("applePayButtonClickCallback", error);
    };

    (async () => {
      try {
        // Include only the specified keys for signature to keep encoder generic
        // Allowed keys per requirement:
        // CallBackUrl, ErrorUrl, WebhookUrl, CustomerReference, DisplayCurrencyIso, Amount, MerchantReference
        const ALLOWED_KEYS = new Set([
          "CallBackUrl",
          "ErrorUrl",
          "WebhookUrl",
          "CustomerReference",
          "DisplayCurrencyIso",
          "Amount",
          "MerchantReferenceId"
        ]);

        // Build the signature payload by iterating allowed keys only
        const filteredForSignature = {};
        for (const key of ALLOWED_KEYS) {
          const value = createPaymentParam[key];
          if (value !== undefined && value !== null) {
            filteredForSignature[key] = value;
          }
        }
        if (Object.keys(filteredForSignature).length === 0) {
          console.warn("[ZidPay] No allowed keys found for signature. Check whitelist and payload values.");
        }
        const createPaymentParamEncoded = await encodeParamsHmacBase64(filteredForSignature, zidPayData?.public_key);
        const customerInfo = {
          external_id: user?.zid_external_id,
          email: customerEmail
          // external_id: "39915",
          // email: "a.mohammed@gmail.com"
        };
        window.zidpayPaymentForm.load(
          config,
          createPaymentParam,
          zidpayErrorCallback,
          applePayButtonClickCallback,
          createPaymentParamEncoded,
          null,
          customerInfo
        );
        setReady(true);
      } catch (e) {
        console.error("Failed to generate payment param signature", e);
      }
    })();
  }, []);

  return (
    <div className="w-100">
      {redirectUrl && <ThreeDSZidpayFrame threeDSUrl={redirectUrl} onRedirect={handleRedirect} />}
      { !user?.zid_external_id && <div className="alert alert-warning">{t("Something went wrong, please contact support")}</div>}
      <div id="root-form" className="w-100">

        <div id="zidpay-payment-form-container-block"></div>
        <div className="d-flex justify-content-center">
          { user?.zid_external_id && <button
            className="btn btn-primary"
            disabled={sendPaymentLoading}
            onClick={handleSendPayment}
          >
            {(sendPaymentLoading || !ready) && <LoadingSpinner/>}{!sendPaymentLoading && t("Pay")} {amount} {t(currency)}
          </button>
          }
        </div>
      </div>
    </div>
  );
}

export default ZidPayPayment;
